import React, { useCallback, useState, useEffect } from "react";
import { Grid, Stack, Typography, Paper } from "@mui/material";
import { ErrorAlert } from "../../../utils";
import { PatientForm, DiagnosisForm } from "./form";
import StepWizard from "react-step-wizard";
import transitions from "./transitions.less";
import _ from "lodash";
import { useSearchParams } from "react-router-dom";
import { useApi, useIsInQuickLink, useNavigate } from "../../../utils/hooks";

const transitionsOptions = {
  enterRight: `${transitions.animated} ${transitions.enterRight}`,
  enterLeft: `${transitions.animated} ${transitions.enterLeft}`,
  exitRight: `${transitions.animated} ${transitions.exitRight}`,
  exitLeft: `${transitions.animated} ${transitions.exitLeft}`,
  intro: `${transitions.animated} ${transitions.intro}`,
};

const NewMedicalDocument = ({ module, MedicineForm, DateForm }) => {
  const { name, basePath } = module;
  const [createStatus, setCreateStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [createdMedicalDocuments, setCreatedMedicalDocuments] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const api = useApi();
  const [searchParams] = useSearchParams();
  const isInQuickLink = useIsInQuickLink();
  const source = isInQuickLink ? 'web-client-embedded' : 'web-client';

  const onFinish = (partialForm) =>
    setForm((form) => ({ ...form, ...partialForm, source }));
  const onAlertClose = () => setCreateStatus(null);

  const create = useCallback(
    (lastForm) => {
      const reference = searchParams.get("reference");
      const formToSubmit = _.assign({}, lastForm, form, { reference });

      setCreateStatus("loading");

      if (basePath === "orders") {
        const promises = formToSubmit.medicines.map((medicineItem) => {
          const singleMedicineForm = {
            ...formToSubmit,
            medicine: medicineItem.text,
            orderTemplate: { id: _.get(medicineItem.template, "id") }
          };
          delete singleMedicineForm.medicines;

          return api.createOrder(singleMedicineForm)
        });
        Promise.all(promises)
          .then((responses) => {
            setCreateStatus("success");
            setCreatedMedicalDocuments(responses);
            return;
          })
          .catch((error) => {
            setCreateStatus("error");
            setError(error);
          });
      } else {
        api.createPrescription(formToSubmit)
          .tap(({ recurring, ...createdPrescription }) => {
            const createdMedicalDocuments =
              !_.isEmpty(recurring)
                ? _(recurring).map(recurringProps => _.assign({}, createdPrescription, recurringProps)).thru(recurringPrescriptions => _.concat(createdPrescription, recurringPrescriptions)).value()
                : [createdPrescription];

            setCreatedMedicalDocuments(createdMedicalDocuments);
            setCreateStatus("success");
          })
          .catch((error) => {
            setCreateStatus("error");
            setError(error);
          });
      }
    },
    [form, basePath, searchParams]
  );

  const isLoading = createStatus === "loading";
  const failed = createStatus === "error";
  const errorMessage = _.get(error, "response.data.message");

  useEffect(() => {
    if (createdMedicalDocuments && createStatus === "success") {
      const id = _.first(createdMedicalDocuments).id;
      navigate(`/details-document/${id}`, {
        state: {
          createdMedicalDocuments: createdMedicalDocuments,
          showSuccess: true,
          backPath: "/"
        },
      });
    }
  }, [createdMedicalDocuments, navigate, createStatus]);

  return (
    <Grid width="100%">
      <Stack
        spacing="1rem"
        sx={{ p: { xs: "1rem", sm: "2rem" }, mt: { xs: "1rem" } }}
      >
        <Typography variant="titleSteps" sx={{ fontSize: { xs: 20, sm: 24 } }}>
          Nueva {name}
          {form?.patient ? (
            <>
              {" | "}
              <Typography
                sx={{
                  display: "inline",
                  color: "secondary.main",
                  fontSize: { xs: 14, sm: 24 },
                }}
              >
                {`${form?.patient?.name}, ${form?.patient?.surname}`}
              </Typography>
            </>
          ) : null}
        </Typography>

        <Stack
          display="flex"
          flexDirection="column"
          alignItems="center"
          spacing="2rem"
          width="100%"
        >
          <div style={{ width: "100%" }}>
            <Paper
              variant="outlined"
              width="100%"
              sx={{
                p: { xs: "20px 10px", sm: "20px 40px" },
                borderRadius: 3,
                mb: 7,
              }}
            >
              {!createdMedicalDocuments && (
                <StepWizard transitions={transitionsOptions}>
                  <PatientForm onFinish={onFinish} />
                  <MedicineForm onFinish={onFinish} fullForm={form} />
                  <DiagnosisForm onFinish={onFinish} />
                  <DateForm onFinish={create} isLoading={isLoading} fullForm={form} />
                </StepWizard>
              )}
            </Paper>
          </div>
        </Stack>
        <ErrorAlert
          show={failed}
          onClose={onAlertClose}
          message={`No se pudo crear la ${name} ${
            errorMessage ? `(${errorMessage})` : ""
          }`}
        />
      </Stack>
    </Grid>
  );
};

export default NewMedicalDocument;
